import React, { createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Container from "../../ui/Container/Container";

import HiddenIcon from "../../assets/icons/HiddenIcon";
import VisibleIcon from "../../assets/icons/VisibleIcon";
import Button from "../../ui/Button/Button";
import Input from "../../ui/Input/Input";
import Link from "../../ui/Link/Link";
import Title from "../../ui/Title/Title";
import { authUser, registerUser } from "../../utils/api/actions/user";
import styles from "./AuthForm.module.scss";
import { ClipLoader } from "react-spinners";

export default function AuthForm() {
  const inputPasswordRef = createRef(null);
  const inputEmailRef = createRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const isLogin = useSelector((state) => state.user.isLogin);

  const [pathname, setPathname] = useState(true);
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fullName, setFullName] = useState("");
  const [parsedData, setParsedData] = useState({
    firstName: "",
    lastName: "",
    patronymic: "",
  });

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [fullNameError, setFullNameError] = useState("");

  useEffect(() => {
    if (location.pathname !== "/") {
      setPathname(false);
    } else {
      setPathname(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/register" && user) navigate("/");
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    if (isLogin) {
      // console.log(user.id);
      // dispatch(getAllDialogs(user.id));
      navigate("/dialog");
    }
  }, [isLogin, navigate]);

  const handlerAuth = (e) => {
    pathname ? navigate("/register") : navigate("/");
  };

  const handlerEmail = (e) => {
    const value = e.target.value;
    setEmail(value);

    if (!value) {
      setEmailError("Введите email");
    } else if (!/\S+@\S+\.\S+/.test(value)) {
      setEmailError("Некорректный email");
    } else {
      setEmailError("");
    }
  };

  const handlerPassword = (e) => {
    const value = e.target.value;
    setPassword(value);

    if (!value) {
      setPasswordError("Введите пароль");
    } else if (value.length < 6 && location.pathname === "/register") {
      setPasswordError("Пароль должен быть не менее 6 символов");
    } else {
      setPasswordError("");
    }
  };

  const handleFullNameChange = (e) => {
    const value = e.target.value;
    setFullName(value);

    const parts = value.trim().split(/\s+/);
    const lastName = parts[0] || "";
    const firstName = parts[1] || "";
    const patronymic = parts.slice(2).join(" ") || "";

    setParsedData({ firstName, lastName, patronymic });

    if (!value) {
      setFullNameError("Введите ФИО");
    } else if (parts.length < 2) {
      setFullNameError("Введите минимум фамилию и имя");
    } else {
      setFullNameError("");
    }
  };

  const sendUserData = () => {
    let valid = true;

    if (!email) {
      setEmailError("Введите email");
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Некорректный email");
      valid = false;
    }

    if (!password) {
      setPasswordError("Введите пароль");
      valid = false;
    } else if (password.length < 6 && location.pathname === "/register") {
      setPasswordError("Пароль должен быть не менее 6 символов");
      valid = false;
    }

    if (location.pathname === "/register") {
      const parts = fullName.trim().split(/\s+/);
      if (!fullName) {
        setFullNameError("Введите ФИО");
        valid = false;
      } else if (parts.length < 2) {
        setFullNameError("Введите минимум фамилию и имя");
        valid = false;
      }
    }

    if (!valid) return;

    setError(""); // Общая ошибка, если нужно

    location.pathname === "/"
      ? dispatch(authUser({ email, password }, setLoading, setError))
      : dispatch(
          registerUser(
            {
              firstName: parsedData.firstName,
              surname: parsedData.lastName,
              patronymic: parsedData.patronymic,
              email,
              password,
            },
            setLoading
          )
        );
  };

  const handlerKeyFullNamePress = (e) => {
    if (e.charCode === 13) inputEmailRef.current.focus();
  };

  const handlerKeyEmailPress = (e) => {
    if (e.charCode === 13) inputPasswordRef.current.focus();
  };

  const handlerKeyPasswordPress = (e) => {
    if (e.charCode === 13) sendUserData();
  };

  const handlerVisiblePassword = () => setVisiblePassword(!visiblePassword);

  return (
    <Container className={`${styles.container}`}>
      <div className={styles.form}>
        <Title
          title={pathname ? "С возвращением!" : "Создать учетную запись"}
          className={`${styles.title}`}
        />
        <div className={styles.inputs}>
          {location.pathname === "/register" && (
            <Input
              label="ФИО"
              className={styles.input}
              onChange={handleFullNameChange}
              value={fullName}
              onKeyPress={handlerKeyFullNamePress}
              type="text"
              autoFocus
              errorMessage={fullNameError}
              isFromAuth
              placeholder="Фамилия Имя Отчество"
            />
          )}

          <Input
            label="Адрес электронной почты"
            className={styles.input}
            onChange={handlerEmail}
            value={email}
            refFocus={inputEmailRef}
            onKeyPress={handlerKeyEmailPress}
            type="text"
            autoFocus={location.pathname === "/"}
            errorMessage={emailError}
            isFromAuth
            placeholder="mail@example.com"
          />

          <Input
            label="Пароль *"
            className={styles.input}
            onChange={handlerPassword}
            value={password}
            refFocus={inputPasswordRef}
            onKeyPress={handlerKeyPasswordPress}
            type={visiblePassword ? "text" : "password"}
            rightIcon={
              visiblePassword ? (
                <VisibleIcon fill={"#006AD7"} />
              ) : (
                <HiddenIcon fill={"#006AD7"} />
              )
            }
            onClickRightIcon={handlerVisiblePassword}
            errorMessage={passwordError}
            isFromAuth
            placeholder="Введите пароль"
          />
        </div>
      </div>

      <div className={styles.errorMessage}>{error}</div>
      <div className={styles.actionsContainer}>
        <Button
          title={
            loading ? "Подождите" : pathname ? "Войти" : "Зарегистрироваться"
          }
          onClick={sendUserData}
          className={`${styles.button}`}
          titleClassName={styles.buttonTitle}
          leftIcon={loading ? <ClipLoader size={20} color="white" /> : null}
        />
        <div className={`${styles.action}`}>
          <span className={`${styles.label}`}>
            {pathname
              ? "У Вас нет учетной записи?"
              : "Уже есть учетная запись?"}
          </span>
          <Link
            title={pathname ? "Зарегистрироваться" : "Вход в систему"}
            onClick={handlerAuth}
          />
        </div>
      </div>
    </Container>
  );
}
